import {
	TextBodyExtraExtraSmall,
	TextBodySmall,
} from '@/components/text/Texts';
import IdGeneratorService from '@/services/id-generator.service';
import { isEmpty } from 'lodash';
import type { CheckboxChangeEvent, CheckboxProps } from 'primereact/checkbox';
import { Checkbox } from 'primereact/checkbox';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import Container, { Column, Row } from '../container/Container';
import styles from './checkbox-input.module.css';
import './form-input.styles.css';
import { cn } from '../ui/cn';

type Props = Partial<CheckboxProps> & {
	checked?: boolean;
	children?: ReactNode;
	className?: string;
	color?: string;
	containerClassName?: string;
	// biome-ignore lint/suspicious/noExplicitAny: Control will have different structure on each use of the TextInput
	control: any;
	formName?: string;
	onChange?: (e: CheckboxChangeEvent) => void;
	id: string;
	text?: string;
	showError?: boolean;
};

const CheckboxInput = ({
	children,
	className = '',
	color = '',
	containerClassName = '',
	control,
	formName,
	id,
	onChange,
	showError = true,
	text,
	...props
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateCheckboxIds(id)
		: idService.generateFormCheckboxIds(id);
	return (
		<Container className={`flex ${containerClassName}`}>
			<Controller
				control={control}
				name={id}
				render={({ field, fieldState: { error } }) => (
					<Row>
						<Checkbox
							checked={props.checked ?? field.value}
							className={`${className} self-center`}
							data-testid={idGenerated.testId}
							id={idGenerated.id}
							inputId={idGenerated.id}
							name={id}
							pt={
								props.pt ?? {
									box: () => ({
										className: styles.defaultCheckboxInput,
									}),
								}
							}
							onChange={
								onChange ? onChange : (e) => field.onChange(e)
							}
							variant="outlined"
							{...props}
						/>
						<Column>
							{children ?? (
								<label
									htmlFor={idGenerated.id}
									className="pl-[12px] align-middle self-center"
								>
									<TextBodySmall
										text={
											text ?? idService.generateLabel(id)
										}
										className={cn('align-middle', color)}
									/>
								</label>
							)}
							{showError && !isEmpty(error) ? (
								<TextBodyExtraExtraSmall
									id={`${idGenerated.id}-helper`}
									className="block pl-[16px] mt-1 text-error"
									text={error.message ?? ''}
								/>
							) : (
								''
							)}
						</Column>
					</Row>
				)}
			/>
		</Container>
	);
};

export default CheckboxInput;
